export interface ReadinessEvaluationItem {
  id: number | string;
  title: string;
  isMatched: boolean;
}

export interface ReadinessEvaluation {
  itemType: ReadinessEvaluationItemTypeEnum;
  /**
   * GR-7. A PROSE SENTENCE, not a boolean.
   *
   * The schema has always declared `userHas: String` and the backend has always
   * produced one (readiness.service.ts composes "High School Diploma, ..."
   * style sentences). This model declared it `boolean`, so any code that
   * trusted the type would have read a non-empty sentence as `true` and an
   * absent one as `false` -- which is nearly right by accident and completely
   * wrong for an occupation whose sentence is "We currently have no information
   * about your training."
   */
  userHas?: string | null;
  /**
   * GR-7. The member's ACTUAL records for this area.
   *
   * This is the field the "Your education" column should have been reading.
   * It is built by composeUserEducation / composeUserWorkExperience from the
   * member's own rows, and it carries the same "omit the title at or below a
   * high school diploma" convention the backend applies to the sentence -- see
   * GR-12, which is that convention being ignored on another surface.
   *
   * Exposed on the schema since ACE.4 and selected by NO frontend operation
   * until QA.7. That is why GR-7 could not be fixed in the mapper alone.
   */
  userHasObject?: EvaluationObject[] | null;
  occupationRequiresObject: {
    id?: string | number;
    title: string;
    details: string;
    detailsArray: string[];
    items: ReadinessEvaluationItem[];
    isMatched: boolean;
  }[];
  /**
   * GR-7. NOT the member's records. These are school programmes whose CIP codes
   * are ADJACENT to the occupation's -- suggestions, computed by joining
   * api.soctocipxref against the programme catalogue. Produced for education
   * only; absent for work experience and training.
   *
   * The mapper used to put these in the "Your education" column and label each
   * one "On your record". Aviation Business Administration appeared on a
   * member's record because a programme somewhere shares a CIP family with
   * their goal.
   */
  otherAlignments: {
    id: string | number;
    title: string;
  }[];
  /**
   * GR-8. Whether the backend judged this area met.
   *
   * Optional because it is genuinely absent on any query that does not select
   * it, and the "Good match" branch must not fire on a missing value.
   */
  isPositiveOutcome?: boolean | null;
}

export interface EvaluationObject {
  id?: number | string | null;
  title?: string | null;
  details?: string | null;
  detailsArray?: string[] | null;
  isMatched?: boolean | null;
}

export enum ReadinessEvaluationItemTypeEnum {
  Education = 0,
  WorkExperience = 1,
  Training = 2,
  Certification = 3,
  Licensing = 4
}
